import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/FeatureCard";
import { Alert, Button } from "../components/Form";
import { useMe } from "../hooks/useMe";
import { api, errorMessage } from "../lib/api";
import { formatMYR } from "../lib/format";
import { locale, t, tk } from "../lib/i18n";
import { GatewayPay } from "../components/GatewayPay";

type Order = { reference: string; email: string; priceSen: number; amountDueSen: number; status: string; holdExpiresAt: string };
type Payment = { gateway?: "stripe" };
type Credits = {
  unlimited: boolean;
  balance: number;
  perPaper: number;
  topup: { priceSen: number; credits: number };
  history: { delta: number; reason: string; at: string }[];
  pendingOrder: Order | null;
  payment: Payment;
};

const REASON: Record<string, string> = {
  licence_grant: tk("Kredit lesen"),
  topup: tk("Tambah kredit"),
  generation: tk("Jana kertas"),
  admin_adjust: tk("Pelarasan admin"),
};

export function CreditsPage() {
  const { me, loading } = useMe();
  return (
    <>
      <PageHeader title={t("Kertas AI")} subtitle={t("1 kredit = 1 kertas soalan yang dijana. Kertas yang gagal dijana, JomBelajar dan muat turun semula PDF tidak ditolak.")} />
      {loading ? (
        <p className="text-sm text-muted">{t("Memuatkan…")}</p>
      ) : !me ? (
        <Alert kind="info"><Link to="/login?next=/kredit" className="underline">{t("Log masuk")}</Link> {t("untuk melihat kredit anda.")}</Alert>
      ) : !me.licenseActive ? (
        <Alert kind="info">{t("Kredit AI datang bersama lesen.")} <Link to="/beli" className="underline">{t("Dapatkan lesen")}</Link>.</Alert>
      ) : (
        <Inner />
      )}
    </>
  );
}

function Inner() {
  const [data, setData] = useState<Credits | null>(null);
  const [order, setOrder] = useState<Order | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    api<Credits>("/api/credits")
      .then((d) => { setData(d); setOrder(d.pendingOrder); })
      .catch((e) => setError(errorMessage(e)));
  }, []);
  useEffect(load, [load]);
  // Back from the payment page: the callback can take a few seconds, so reload the balance a few times.
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("bayar") !== "1") return;
    let n = 0;
    const id = setInterval(() => { if (++n > 10) clearInterval(id); else load(); }, 3000);
    return () => clearInterval(id);
  }, [load]);

  async function startTopup() {
    setBusy(true); setError(null);
    try {
      const r = await api<{ order: Order }>("/api/credits/topup", { method: "POST" });
      setOrder(r.order);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  if (!data) return error ? <Alert kind="error">{error}</Alert> : <p className="text-sm text-muted">{t("Memuatkan…")}</p>;

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <section className="space-y-4">
        <div className="rounded-card border border-surface-line bg-card p-5">
          <p className="text-sm text-muted">{t("Baki kertas AI")}</p>
          {data.unlimited ? (
            <p className="text-3xl font-bold">{t("Tanpa had")} <span className="text-base font-normal text-muted">(admin)</span></p>
          ) : (
            <>
              <p className={`text-4xl font-bold tabular-nums ${data.balance < 1 ? "text-red-700" : ""}`}>{data.balance.toLocaleString(locale())} <span className="text-base font-normal text-muted">{t("kertas")}</span></p>
            </>
          )}
        </div>

        <div className="rounded-card border border-surface-line bg-card p-5">
          <h2 className="font-semibold">{t("Tambah kertas")}</h2>
          <p className="mt-1 text-sm text-muted">
            {t("{price} untuk {n} kertas. Lesen anda kekal sama.", { price: formatMYR(data.topup.priceSen), n: data.topup.credits.toLocaleString(locale()) })}
          </p>
          {!order && (
            <Button onClick={startTopup} busy={busy} className="mt-3 w-full">{t("Tambah {n} kertas — {price}", { n: data.topup.credits.toLocaleString(locale()), price: formatMYR(data.topup.priceSen) })}</Button>
          )}
          {order && (
            <div className="mt-4 space-y-3">
              {order.status === "awaiting_verification" ? (
                <Alert kind="success">{t("Bayaran diterima untuk semakan. Kredit akan ditambah selepas disahkan.")}</Alert>
              ) : data.payment.gateway && order.status === "pending" ? (
                <GatewayPay reference={order.reference} email={order.email} />
              ) : (
                <Alert kind="error">{t("Pembayaran dalam talian tidak tersedia buat masa ini. Sila cuba lagi sebentar nanti.")}</Alert>
              )}
            </div>
          )}
          {error && <div className="mt-3"><Alert kind="error">{error}</Alert></div>}
        </div>
      </section>

      <section className="rounded-card border border-surface-line bg-card p-5">
        <h2 className="font-semibold">{t("Sejarah kredit")}</h2>
        {data.history.length === 0 && <p className="mt-2 text-sm text-muted">{t("Tiada rekod lagi.")}</p>}
        <ul className="mt-2 divide-y divide-surface-line text-sm">
          {data.history.map((h, i) => (
            <li key={i} className="flex items-center justify-between py-2">
              <span>
                {REASON[h.reason] ? t(REASON[h.reason]!) : h.reason}
                <span className="block text-xs text-muted">
                  {new Date(h.at).toLocaleString(locale(), { timeZone: "Asia/Kuala_Lumpur", dateStyle: "medium", timeStyle: "short" })}
                </span>
              </span>
              <span className={`tabular-nums font-semibold ${h.delta > 0 ? "text-emerald-700" : "text-muted"}`}>
                {h.delta > 0 ? "+" : ""}{h.delta.toLocaleString(locale())}
              </span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
